import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '../contexts/ToastContext';
import { fmtDate } from '../utils/formatUtils';
import {
  bepaalTopset,
  gemiddeldeRpe,
  loggerLabel,
  maandGrenzenAlsDatum,
  oefeningKeuzes,
  oefeningenVan,
  sessieDatum,
  topsetLabel,
  vatOefeningSamen,
} from '../utils/workoutHistoryUtils';
import { errorText } from '../utils/errorMessages';
import { t } from '../i18n/nl.js';

// Aantal sessies per "pagina". De historie groeit per cliënt door, dus de
// lijstquery heeft altijd een limiet — "meer laden" verhoogt die stapsgewijs.
const PAGE_SIZE = 10;

const nlRpe = new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export default function ClientWorkoutHistoryPanel({ clientId, clientNaam = null }) {
  const { showError } = useToast();
  const [sessies, setSessies] = useState([]);
  const [coaches, setCoaches] = useState({});
  const [totaal, setTotaal] = useState(0);
  const [sessiesDezeMaand, setSessiesDezeMaand] = useState(0);
  const [limiet, setLimiet] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [gekozenOefening, setGekozenOefening] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // performed_on is een `date`; de maandgrenzen worden daarom als kale
      // datumstring vergeleken (de ISO-tijdstempels van maandGrenzen()
      // bevatten een tijdzone-offset die PostgREST op een date-kolom niet
      // nodig heeft).
      const { van, tot } = maandGrenzenAlsDatum();
      const [sessiesRes, maandRes] = await Promise.all([
        // Eén geneste call (sessie → oefeningen → sets), het bestaande
        // patroon van ClientTemplatesPanel/ClientTemplateFormPage. Altijd
        // begrensd door `limiet`; count=exact levert het totaal voor de
        // "meer laden"-knop zonder tweede query.
        supabase
          .from('workout_sessions')
          .select(
            'id, performed_on, completed_at, created_at, notes, coach_id, logged_by, '
            + 'template:client_exercise_templates(id, name), '
            // exercise_type staat bewust op de sessie-oefening zelf (review
            // M11): dat is het snapshot van het moment van loggen. De live
            // waarde uit exercises blijft als fallback in de select staan
            // voor rijen van vóór migratie 20260815170000.
            + 'workout_session_exercises(id, sort_order, coach_note, exercise_type, exercises(id, name, exercise_type), '
            + 'workout_sets(id, set_number, reps, weight, rpe, '
            + 'duration_seconds, distance_meters, speed_value, incline_percent, resistance_level, heart_rate_avg, calories))',
            { count: 'exact' },
          )
          .eq('client_id', clientId)
          .not('completed_at', 'is', null)
          // Onderdeel 1: sorteren op de trainingsdatum, niet op het moment
          // waarop de registratie werd afgerond.
          .order('performed_on', { ascending: false })
          .order('id', { ascending: false })
          .limit(limiet),
        supabase
          .from('workout_sessions')
          .select('id', { count: 'exact', head: true })
          .eq('client_id', clientId)
          .not('completed_at', 'is', null)
          .gte('performed_on', van)
          .lt('performed_on', tot),
      ]);

      if (sessiesRes.error) throw sessiesRes.error;
      if (maandRes.error) throw maandRes.error;

      const rijen = sessiesRes.data || [];
      setSessies(rijen);
      setTotaal(sessiesRes.count ?? rijen.length);
      setSessiesDezeMaand(maandRes.count ?? 0);

      // coach_basic_profiles is de kolom-veilige route naar stafnamen
      // (zelfde aanpak als OefeningenPage).
      const coachIds = [...new Set(rijen.map(r => r.coach_id).filter(Boolean))];
      if (coachIds.length) {
        const { data: profielen, error: profielErr } = await supabase
          .from('coach_basic_profiles')
          .select('id, full_name')
          .in('id', coachIds);
        if (profielErr) throw profielErr;
        setCoaches(Object.fromEntries((profielen || []).map(p => [p.id, p.full_name])));
      } else {
        setCoaches({});
      }
    } catch (err) {
      // Onderdeel 10: nooit de ruwe PostgREST-melding tonen.
      showError(t('trainingHistory.foutLaden') + errorText(err, { scope: 'ClientWorkoutHistoryPanel' }));
    } finally {
      setLoading(false);
    }
  }, [clientId, limiet, showError]);

  useEffect(() => { load(); }, [load]);

  // Keuzelijst voor de topset-kaart. De lijst groeit mee met "meer laden" —
  // bewust, want een ongelimiteerde query over de hele historie zou precies
  // zijn wat dit scherm juist vermijdt.
  const keuzes = useMemo(() => oefeningKeuzes(sessies), [sessies]);

  // Automatisch de meest recente oefening zolang de coach niets koos (of zijn
  // keuze buiten het geladen venster valt).
  const actieveOefening = keuzes.some(o => o.id === gekozenOefening)
    ? gekozenOefening
    : (keuzes[0]?.id || '');

  const topset = useMemo(() => bepaalTopset(sessies, actieveOefening), [sessies, actieveOefening]);
  const rpeLaatsteSessie = useMemo(() => gemiddeldeRpe(sessies[0]), [sessies]);
  const topsetTekst = topsetLabel(topset);

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{t('trainingHistory.titel')}</h2>
        <p className="text-muted" style={{ fontSize: 13, marginTop: 2 }}>{t('trainingHistory.subtitel')}</p>
      </div>

      {loading && sessies.length === 0 ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <>
          <div className="workout-stats">
            <div className="workout-stat">
              <span className="workout-stat-label">{t('trainingHistory.statSessiesTitel')}</span>
              <strong className="workout-stat-value">{sessiesDezeMaand}</strong>
              <span className="workout-stat-sub">
                {t(sessiesDezeMaand === 1 ? 'trainingHistory.statSessiesEenheidEnkel' : 'trainingHistory.statSessiesEenheid')}
              </span>
            </div>

            <div className="workout-stat">
              <span className="workout-stat-label">{t('trainingHistory.statTopsetTitel')}</span>
              <strong className={`workout-stat-value${topsetTekst ? '' : ' is-leeg'}`}>
                {topsetTekst || t('trainingHistory.leegWaarde')}
              </strong>
              {keuzes.length > 0 ? (
                <select
                  className="form-input workout-stat-select"
                  aria-label={t('trainingHistory.statTopsetKiesLabel')}
                  value={actieveOefening}
                  onChange={event => setGekozenOefening(event.target.value)}
                >
                  {keuzes.map(o => (
                    <option key={o.id} value={o.id}>{o.naam}</option>
                  ))}
                </select>
              ) : (
                <span className="workout-stat-sub">{t('trainingHistory.statTopsetLeeg')}</span>
              )}
              {topsetTekst && topset?.datum && (
                <span className="workout-stat-sub">
                  {t('trainingHistory.statTopsetDatum', { datum: fmtDate(topset.datum) })}
                </span>
              )}
            </div>

            <div className="workout-stat">
              <span className="workout-stat-label">{t('trainingHistory.statRpeTitel')}</span>
              <strong className={`workout-stat-value${rpeLaatsteSessie ? '' : ' is-leeg'}`}>
                {rpeLaatsteSessie ? nlRpe.format(rpeLaatsteSessie.gemiddelde) : t('trainingHistory.leegWaarde')}
              </strong>
              <span className="workout-stat-sub">
                {rpeLaatsteSessie
                  ? t('trainingHistory.statRpeToelichting', { aantal: rpeLaatsteSessie.aantal })
                  : t(sessies.length === 0 ? 'trainingHistory.leegNogGeenSessies' : 'trainingHistory.statRpeLeeg')}
              </span>
            </div>
          </div>

          {sessies.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: 32 }}>📋</div>
              <p style={{ fontWeight: 600 }}>{t('trainingHistory.geenSessiesTitel')}</p>
              <p className="text-muted" style={{ maxWidth: 460, margin: '4px auto 0' }}>
                {t('trainingHistory.geenSessiesTekst')}
              </p>
            </div>
          ) : (
            <>
              <div className="workout-sessions">
                {sessies.map(sessie => {
                  const oefeningen = oefeningenVan(sessie);
                  return (
                    <article key={sessie.id} className="workout-session">
                      <div className="workout-session-head">
                        <h3>{fmtDate(sessieDatum(sessie))}</h3>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <span className="badge badge-active">
                            {sessie.template?.name || t('trainingHistory.losseSessie')}
                          </span>
                          <span className={`badge ${sessie.logged_by === 'client' ? 'badge-zelfstandig' : 'badge-coach-log'}`}>
                            {sessie.logged_by === 'client' ? t('trainingHistory.badgeZelfstandig') : t('trainingHistory.badgeDoorCoach')}
                          </span>
                        </div>
                      </div>
                      <p className="workout-session-meta">
                        {loggerLabel(sessie, coaches, clientNaam)}
                      </p>

                      {oefeningen.length === 0 ? (
                        <p className="workout-session-meta">{t('trainingHistory.geenOefeningen')}</p>
                      ) : (
                        <ul className="workout-session-oefeningen">
                          {oefeningen.map(oefening => (
                            <li key={oefening.id}>
                              <div>
                                <span className="workout-session-oefening-naam">{oefening.exercises?.name}</span>
                                {oefening.sets.length > 0 && (
                                  <span className="workout-session-oefening-sets">{vatOefeningSamen(oefening)}</span>
                                )}
                              </div>
                              {oefening.coach_note && (
                                <span className="workout-session-oefening-note">
                                  {t('trainingHistory.aandachtspunt', { tekst: oefening.coach_note })}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}

                      {sessie.notes && (
                        <blockquote className="workout-session-notitie">{sessie.notes}</blockquote>
                      )}
                    </article>
                  );
                })}
              </div>

              <div className="workout-sessions-footer">
                <span className="text-muted" style={{ fontSize: 12 }}>
                  {t('trainingHistory.aantalGetoond', { getoond: sessies.length, totaal })}
                </span>
                {sessies.length < totaal && (
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={loading}
                    onClick={() => setLimiet(huidig => huidig + PAGE_SIZE)}
                  >
                    {t('trainingHistory.meerLaden')}
                  </button>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
